import { memo, useState } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { usePartnershipData } from './usePartnershipData.jsx';

export const CrossProgramChart = memo(function CrossProgramChart() {
  const { crossProgramDataByFunding, localFilteredResults } = usePartnershipData();
  const matrixData = crossProgramDataByFunding;
  const [tip, setTip] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  if (!matrixData || !matrixData.pairs || matrixData.pairs.length === 0) return (
    <ChartWrapper title="Cross-Program Co-contributions">
      <NoChartData message="No cross-program co-contributions found" detail="This heatmap shows how programs co-contribute to each other's results using the Contributing Initiatives field. No cross-program sharing was found — results may all belong to a single program." />
    </ChartWrapper>
  );

  const topPairs = matrixData.pairs.slice(0, 15);
  const maxCount = topPairs[0]?.count || 1;

  const chartData = topPairs.map(p => ({
    pair: `${p.source} \u2194 ${p.target}`,
    sourceName: p.sourceName,
    targetName: p.targetName,
    count: p.count,
    w1w2: p.w1w2Count,
    w3: p.w3Count,
  }));

  return (
    <ChartWrapper title="Cross-Program Collaboration" info="Most frequent program-to-program collaborations based on shared result contributions. Higher bars indicate stronger cross-program links." chartData={chartData} sourceResults={localFilteredResults} exportFilename="cross-program-collaboration">
      <div className="space-y-2">
        {topPairs.map((pair, idx) => {
          const w1w2Pct = (pair.w1w2Count / maxCount) * 100;
          const w3Pct = (pair.w3Count / maxCount) * 100;
          return (
            <div key={idx} className="flex items-center gap-3"
              onMouseEnter={(e) => { setTip(pair); setTipPos({ x: e.clientX, y: e.clientY }); }}
              onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setTip(null)}
              style={{ cursor: 'default' }}
            >
              <div className="w-28 sm:w-40 text-xs text-gray-700 font-medium truncate">
                {pair.source} <span className="text-gray-400">{'\u2194'}</span> {pair.target}
              </div>
              <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden flex">
                {pair.w1w2Count > 0 && (
                  <div
                    className="h-full transition-all"
                    style={{ width: `${w1w2Pct}%`, backgroundColor: '#006837' }}
                  />
                )}
                {pair.w3Count > 0 && (
                  <div
                    className="h-full transition-all"
                    style={{ width: `${w3Pct}%`, backgroundColor: '#F37021', borderRadius: pair.w1w2Count === 0 ? '9999px 0 0 9999px' : '0' }}
                  />
                )}
              </div>
              <div className="w-12 text-xs font-semibold text-gray-600 text-right">{pair.count}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-gray-400">Co-contributions: results where both programs are listed as initiative or contributor</p>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#006837' }} />
            <span>W1/W2</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F37021' }} />
            <span>W3</span>
          </div>
        </div>
      </div>
      {tip && (
        <div className="fixed z-50 pointer-events-none" style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}>
          <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{tip.sourceName} ↔ {tip.targetName}</div>
            {tip.w1w2Count > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#006837', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ color: '#4b5563' }}>W1/W2: <strong>{tip.w1w2Count.toLocaleString()}</strong></span>
              </div>
            )}
            {tip.w3Count > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F37021', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ color: '#4b5563' }}>W3/Bilateral: <strong>{tip.w3Count.toLocaleString()}</strong></span>
              </div>
            )}
            <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: 12 }}>Total: {tip.count.toLocaleString()}</div>
          </div>
        </div>
      )}
    </ChartWrapper>
  );
});
